import React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, getApiErrorMessage } from '../lib/api'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import SurveySchemaBuilder from '../components/SurveySchemaBuilder'

function unwrap(res) {
  return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
}

function buildSurveySources(catalog, myTemplates) {
  const baseSources = (Array.isArray(catalog) ? catalog : [])
    .filter((item) => item?.ready && item?.template?.schema)
    .map((item) => ({
      id: `catalog-${item.template.id}`,
      kind: 'catalog',
      name: item.template.name || item.category?.name || '기본 설문',
      description: item.category?.name || '',
      schema: item.template.schema,
    }))

  const customSources = (Array.isArray(myTemplates) ? myTemplates : [])
    .filter((item) => item?.id && item?.schema)
    .map((item) => ({
      id: `custom-${item.id}`,
      kind: 'custom',
      name: item.name || '내 맞춤 설문',
      description: '내가 만든 설문',
      schema: item.schema,
    }))

  return [...customSources, ...baseSources]
}

function CreateModePicker({ canImport, onChoose }) {
  return (
    <div className="mx-auto max-w-4xl space-y-6 py-4">
      <div>
        <div className="text-sm text-slate-500">
          <Link to="/support/surveys?tab=list" className="hover:text-brand-sky">설문관리</Link>
          <span className="mx-2 text-slate-300">/</span>
          <span className="text-slate-700">설문 생성</span>
        </div>
        <h1 className="mt-3 text-2xl font-bold text-slate-900">새 설문 만들기</h1>
      </div>

      <div className="grid gap-4 md:grid-cols-2" role="group" aria-label="설문 생성 방식">
        <button
          type="button"
          className="rounded-3xl border border-slate-200 bg-white p-6 text-left shadow-sm transition hover:border-brand-sky hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-brand-sky/30"
          onClick={() => onChoose('new')}
        >
          <div className="text-sm font-semibold text-brand-navy">새로 만들기</div>
          <div className="mt-2 text-xl font-bold text-slate-900">빈 설문에서 시작</div>
          <p className="mt-3 text-sm leading-6 text-slate-600">설문 항목과 질문을 직접 추가해 원하는 구성으로 만듭니다.</p>
        </button>
        <button
          type="button"
          disabled={!canImport}
          className="rounded-3xl border border-slate-200 bg-white p-6 text-left shadow-sm transition hover:border-brand-sky hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-brand-sky/30 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => onChoose('import')}
        >
          <div className="text-sm font-semibold text-brand-navy">기존 설문 불러오기</div>
          <div className="mt-2 text-xl font-bold text-slate-900">기존 구성에서 시작</div>
          <p className="mt-3 text-sm leading-6 text-slate-600">기본 설문 또는 내 맞춤 설문을 가져와 새 사본으로 수정합니다.</p>
        </button>
      </div>
      {!canImport && <p className="text-sm text-slate-500">현재 불러올 수 있는 설문이 없습니다. 새 설문으로 시작해 주세요.</p>}
    </div>
  )
}

function SourceList({ title, sources, selectedId, onSelect }) {
  if (sources.length === 0) return null
  return (
    <section>
      <h2 className="text-sm font-semibold text-slate-700">{title}</h2>
      <div className="mt-2 grid gap-2">
        {sources.map((source) => {
          const selected = source.id === selectedId
          return (
            <button
              key={source.id}
              type="button"
              aria-pressed={selected}
              className={`rounded-2xl border px-4 py-3 text-left transition ${selected ? 'border-brand-navy bg-sky-50 ring-1 ring-brand-navy/10' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
              onClick={() => onSelect(source.id)}
            >
              <div className="font-semibold text-slate-900">{source.name}</div>
              {source.description && <div className="mt-1 text-sm text-slate-500">{source.description}</div>}
            </button>
          )
        })}
      </div>
    </section>
  )
}

function ImportSurveyPicker({ sources, selectedId, onSelect, onBack, onContinue }) {
  const customSources = sources.filter((source) => source.kind === 'custom')
  const baseSources = sources.filter((source) => source.kind === 'catalog')
  return (
    <div className="mx-auto max-w-3xl space-y-5 py-4">
      <div>
        <button type="button" className="text-sm text-slate-500 hover:text-brand-sky" onClick={onBack}>생성 방식 선택으로</button>
        <h1 className="mt-3 text-2xl font-bold text-slate-900">기존 설문 불러오기</h1>
      </div>
      <div className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5">
        <div className="space-y-5">
          <SourceList title="내 맞춤 설문" sources={customSources} selectedId={selectedId} onSelect={onSelect} />
          <SourceList title="카테고리별 기본 설문" sources={baseSources} selectedId={selectedId} onSelect={onSelect} />
        </div>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className="rounded-lg border px-4 py-2 text-sm hover:bg-slate-50" onClick={onBack}>이전</button>
        <button type="button" className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50" disabled={!selectedId} onClick={onContinue}>불러와서 수정</button>
      </div>
    </div>
  )
}

export default function SurveyCreationPage({ editMode = false }) {
  const navigate = useNavigate()
  const { id: rawId } = useParams()
  const editingTemplateId = editMode ? Number(String(rawId || '').trim()) : NaN
  const hasValidEditingTemplateId = Number.isFinite(editingTemplateId) && editingTemplateId > 0
  const queryClient = useQueryClient()
  const [mode, setMode] = React.useState('')
  const [selectedSourceId, setSelectedSourceId] = React.useState('')
  const [importConfirmed, setImportConfirmed] = React.useState(false)

  const catalogQuery = useQuery({
    queryKey: ['counsel-surveys', 'catalog'],
    enabled: !editMode,
    queryFn: async () => unwrap(await api.get('/counsel-surveys/catalog', { __silent: true })),
  })
  const myTemplatesQuery = useQuery({
    queryKey: ['counsel-surveys', 'my-templates', 'list'],
    enabled: !editMode,
    queryFn: async () => unwrap(await api.get('/counsel-surveys/my-templates', { __silent: true })),
  })

  const editTemplateQuery = useQuery({
    queryKey: ['counsel-surveys', 'my-templates', 'detail', editingTemplateId],
    enabled: editMode && hasValidEditingTemplateId,
    queryFn: async () => unwrap(await api.get(`/counsel-surveys/my-templates/${editingTemplateId}`, { __silent: true })),
  })
  const sources = React.useMemo(
    () => buildSurveySources(catalogQuery.data, myTemplatesQuery.data),
    [catalogQuery.data, myTemplatesQuery.data],
  )
  const selectedSource = React.useMemo(
    () => sources.find((source) => source.id === selectedSourceId) || null,
    [sources, selectedSourceId],
  )

  const createMutation = useMutation({
    mutationFn: async ({ name, schema, imported }) => unwrap(await api.post('/counsel-surveys/my-templates', {
      name,
      schema,
      publish: true,
      changeSummary: imported ? '기존 설문을 불러와 새로 생성' : '새 설문 생성',
    })),
    onSuccess: async () => {
      try { window?.toast?.show?.('새 설문을 저장했습니다.', 'success') } catch {}
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['counsel-surveys', 'catalog'] }),
        queryClient.invalidateQueries({ queryKey: ['counsel-surveys', 'my-templates', 'list'] }),
      ])
      navigate('/support/surveys?tab=list')
    },
    onError: (error) => {
      try { window?.toast?.show?.(getApiErrorMessage(error, '설문을 저장하지 못했습니다.'), 'error') } catch {}
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ name, schema }) => unwrap(await api.patch(`/counsel-surveys/my-templates/${editingTemplateId}`, {
      name,
      schema,
      publish: true,
      changeSummary: '맞춤 설문 편집',
    })),
    onSuccess: async () => {
      try { window?.toast?.show?.('설문 구성을 저장했습니다.', 'success') } catch {}
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['counsel-surveys', 'catalog'] }),
        queryClient.invalidateQueries({ queryKey: ['counsel-surveys', 'my-templates', 'list'] }),
        queryClient.invalidateQueries({ queryKey: ['counsel-surveys', 'my-templates', 'detail', editingTemplateId] }),
      ])
      navigate('/support/surveys?tab=list')
    },
    onError: (error) => {
      try { window?.toast?.show?.(getApiErrorMessage(error, '설문을 저장하지 못했습니다.'), 'error') } catch {}
    },
  })

  if (editMode) {
    if (!hasValidEditingTemplateId) {
      return <InlineError message="올바른 설문 정보가 아닙니다." />
    }
    if (editTemplateQuery.isLoading) return <Loading />
    if (editTemplateQuery.isError || !editTemplateQuery.data) {
      return <InlineError message="맞춤 설문을 불러오지 못했습니다." />
    }

    const template = editTemplateQuery.data
    return (
      <div className="pb-10">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm text-slate-500">
              <Link to="/support/surveys?tab=list" className="hover:text-brand-sky">설문관리</Link>
              <span className="mx-2 text-slate-300">/</span>
              <span className="text-slate-700">맞춤 설문 편집</span>
            </div>
            <h1 className="mt-3 text-2xl font-bold text-slate-900">맞춤 설문 편집</h1>
          </div>
        </div>
        <SurveySchemaBuilder
          key={`edit-${template.id || editingTemplateId}`}
          initialSchema={template.schema}
          initialName={template.name || ''}
          preserveInitialTitle
          onCancel={() => navigate('/support/surveys?tab=list')}
          onSave={(payload) => updateMutation.mutate(payload)}
          saving={updateMutation.isPending}
        />
      </div>
    )
  }
  if (catalogQuery.isLoading || myTemplatesQuery.isLoading) return <Loading />
  if (catalogQuery.isError && myTemplatesQuery.isError) {
    return <InlineError message="설문 목록을 불러오지 못했습니다." />
  }

  if (!mode) {
    return <CreateModePicker canImport={sources.length > 0} onChoose={(nextMode) => {
      setSelectedSourceId('')
      setImportConfirmed(false)
      setMode(nextMode)
    }} />
  }

  if (mode === 'import' && !importConfirmed) {
    return (
      <ImportSurveyPicker
        sources={sources}
        selectedId={selectedSourceId}
        onSelect={setSelectedSourceId}
        onBack={() => {
          setSelectedSourceId('')
          setMode('')
        }}
        onContinue={() => setImportConfirmed(true)}
      />
    )
  }

  const isImported = mode === 'import' && importConfirmed
  const sourceName = selectedSource?.name || ''
  return (
    <div className="pb-10">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-500">
          <Link to="/support/surveys?tab=list" className="hover:text-brand-sky">설문관리</Link>
          <span className="mx-2 text-slate-300">/</span>
          <span className="text-slate-700">{isImported ? '기존 설문 수정 후 생성' : '새 설문 만들기'}</span>
        </div>
        <button
          type="button"
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          onClick={() => {
            setSelectedSourceId('')
            setImportConfirmed(false)
            setMode(isImported ? 'import' : '')
          }}
        >
          {isImported ? '가져올 설문 다시 선택' : '생성 방식 바꾸기'}
        </button>
      </div>
      <SurveySchemaBuilder
        key={isImported ? selectedSource.id : 'new'}
        initialSchema={isImported ? selectedSource.schema : null}
        initialName={isImported ? sourceName : ''}
        sourceLabel={isImported ? sourceName : ''}
        onCancel={() => {
          setSelectedSourceId('')
          setImportConfirmed(false)
          setMode(isImported ? 'import' : '')
        }}
        onSave={(payload) => createMutation.mutate({ ...payload, imported: isImported })}
        saving={createMutation.isPending}
      />
    </div>
  )
}
